import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Hash, Newspaper } from "lucide-react";
import { useLanguage } from "../i18n";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export function BlogSidebar({ current, posts = [] }) {
  const { t } = useLanguage();

  const related = useMemo(() => {
    if (!current) return [];
    const tags = new Set(current.tags || []);
    const cats = new Set(current.categories || []);
    return posts
      .filter((p) => p.post_id !== current.post_id)
      .map((p) => ({
        ...p,
        _score: (p.tags || []).filter((tag) => tags.has(tag)).length * 3
          + (p.categories || []).filter((cat) => cats.has(cat)).length * 2,
      }))
      .sort((a, b) => b._score - a._score || String(b.publish_at || b.created_at).localeCompare(String(a.publish_at || a.created_at)))
      .slice(0, 5);
  }, [current, posts]);

  const tags = useMemo(() => {
    const out = [];
    [...(current?.tags || []), ...related.flatMap((p) => p.tags || [])].forEach((tag) => {
      if (tag && !out.includes(tag)) out.push(tag);
    });
    return out.slice(0, 10);
  }, [current, related]);

  if (!related.length && !tags.length) return null;

  return (
    <aside data-testid="article-sticky-sidebar" className="lg:sticky lg:top-24 h-fit space-y-5">
      {related.length > 0 && (
        <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-5">
          <div className="flex items-center gap-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF6B35]">
            <Newspaper className="w-4 h-4" /> {t("blog.moreStories")}
          </div>
          <p className="mt-2 text-xs text-slate-500">{t("blog.moreStoriesSub")}</p>
          <div className="mt-5 space-y-3">
            {related.map((p, i) => (
              <Link key={p.post_id} to={`/blog/${p.slug}`}
                data-testid={i === 0 ? "article-next-preview-card" : `article-related-card-${p.slug}`}
                className="group flex items-center gap-3 rounded-2xl border border-slate-100 p-2.5 hover:border-[#FF6B35]/40 hover:bg-[#FAF5EE] transition-colors">
                {p.image ? (
                  <img src={imgSrc(p.image)} alt="" loading="lazy"
                    className="w-16 h-16 rounded-xl object-cover shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-[#1E293B] flex items-center justify-center shrink-0">
                    <Newspaper className="w-5 h-5 text-white/40" />
                  </div>
                )}
                <span className="min-w-0 flex-1 font-display font-bold text-sm leading-snug text-slate-800 line-clamp-2 group-hover:text-[#FF6B35] transition-colors">
                  {p.title}
                </span>
                <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-[#FF6B35] shrink-0 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {tags.length > 0 && (
        <div className="rounded-3xl bg-[#F3ECE2] p-5">
          <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">{t("blog.autoTags")}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Link key={tag} to={`/blog?tag=${encodeURIComponent(tag)}`} data-testid={`auto-tag-pill-${tag}`}
                className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-[0.14em] text-slate-600 hover:bg-[#FF6B35] hover:text-white transition-colors">
                <Hash className="w-3 h-3" /> {tag}
              </Link>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
